import { useEffect, useRef, useCallback } from 'react'

/**
 * Calls `fn` once, `ms` after mount (or after `open` turns true).
 *
 * The latest `fn` is read from a ref, so a parent that passes a NEW function on
 * every render does not restart the countdown. It used to: the toast wrapper
 * hands each toast a fresh `onClose` whenever any toast appears or goes, so
 * every new toast reset the others to a full 3 seconds and a busy page kept
 * them on screen indefinitely (owner, 08/10: "Notifications don't disappear").
 */
function useTimeout(fn, ms = 0, open = true) {
	const timeout = useRef<ReturnType<typeof setTimeout> | undefined>()
	const fnRef = useRef(fn)

	useEffect(() => {
		fnRef.current = fn
	})

	const clear = useCallback(() => {
		if (timeout.current) clearTimeout(timeout.current)
		timeout.current = undefined
	}, [])

	const set = useCallback(() => {
		clear()
		if (open) {
			timeout.current = setTimeout(() => fnRef.current?.(), ms)
		}
	}, [ms, open, clear])

	useEffect(() => {
		set()
		return clear
	}, [set, clear])

	return { clear, reset: set }
}

export default useTimeout
